/**
 * Build or edit an audience — a reusable "who should receive this" rule.
 *
 * Mirrors the Task Rules builder on the Action Center: each condition is a checkbox that reveals
 * its inputs, and everything set combines with AND. The Kind toggle swaps which half of the
 * conditions is shown, because patient and lead predicates never mix.
 *
 * The Preview button is the safeguard — it evaluates the UNSAVED rule against live data so the
 * count can be sanity-checked before anyone wires it to a template.
 */
import React, { useState } from 'react';
import { X, Users, Eye, Save, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  useCreateAudienceMutation,
  useUpdateAudienceMutation,
  usePreviewAudienceDraftMutation,
} from '../../api/campaignsApi';

const PATIENT_STATUS = ['ActiveTreatment', 'Recovering', 'OnHold', 'Completed', 'Dropped', 'Referred'];
const LEAD_STATUS = [
  'New', 'Contacted', 'FollowUpPending', 'Qualified', 'ConsultationScheduled', 'NoShow',
  'ConsultationCompleted', 'TreatmentStarted', 'ClosedLost', 'JunkLead', 'NotConnected',
  'OutOfRadius', 'NotConverted',
];
const LEAD_SOURCE = [
  'MetaAds', 'InstagramAds', 'Facebook', 'GoogleAds', 'WebsiteForm', 'WhatsApp',
  'WalkIn', 'PhoneCall', 'Referral', 'QRCode', 'OfflineCamp', 'Other',
];
const LEAD_PRIORITY = ['Cold', 'Warm', 'Hot'];

const apiError = (e) =>
  e?.data?.error?.message
  || Object.values(e?.data?.error?.errors ?? {}).flat()[0]
  || 'Something went wrong.';

const num = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, n) : fallback;
};

export default function AudienceBuilderModal({ audience, onClose }) {
  const isNew = !audience;
  const [form, setForm] = useState(() => ({
    name: audience?.name ?? '',
    description: audience?.description ?? '',
    kind: audience?.kind ?? 'Patient',

    useLastVisit: !!audience?.lastVisitOp,
    lastVisitOp: audience?.lastVisitOp ?? 'Between',
    lastVisitDaysFrom: audience?.lastVisitDaysFrom ?? 20,
    lastVisitDaysTo: audience?.lastVisitDaysTo ?? 15,

    useSessions: audience?.sessionsRemaining != null,
    sessionsRemainingOp: audience?.sessionsRemainingOp ?? 'Lte',
    sessionsRemaining: audience?.sessionsRemaining ?? 2,

    noUpcomingOnly: audience?.noUpcomingOnly ?? false,
    renewalNotPitchedOnly: audience?.renewalNotPitchedOnly ?? false,
    requireCompletedAppointment: audience?.requireCompletedAppointment ?? true,

    useDue: audience?.minDue != null,
    minDue: audience?.minDue ?? 1000,

    useCreatedRange: !!(audience?.createdFrom || audience?.createdTo),
    createdFrom: audience?.createdFrom ? audience.createdFrom.slice(0, 10) : '',
    createdTo: audience?.createdTo ? audience.createdTo.slice(0, 10) : '',

    usePatientStatus: !!audience?.patientStatus,
    patientStatus: audience?.patientStatus ?? 'ActiveTreatment',

    useLeadStatus: !!audience?.leadStatus,
    leadStatus: audience?.leadStatus ?? 'FollowUpPending',
    useLeadSource: !!audience?.leadSource,
    leadSource: audience?.leadSource ?? 'MetaAds',
    useLeadPriority: !!audience?.leadPriority,
    leadPriority: audience?.leadPriority ?? 'Warm',

    useFollowUp: !!audience?.followUpOp,
    followUpOp: audience?.followUpOp ?? 'Gte',
    followUpDaysFrom: audience?.followUpDaysFrom ?? 3,
    followUpDaysTo: audience?.followUpDaysTo ?? 0,
    openFollowUpOnly: audience?.openFollowUpOnly ?? true,

    includeArchivedLeads: audience?.includeArchivedLeads ?? false,
    includeConvertedLeads: audience?.includeConvertedLeads ?? false,

    useSuppression: audience?.suppressionWindowDays != null,
    suppressionWindowDays: audience?.suppressionWindowDays ?? 7,
  }));

  const [preview, setPreview] = useState(null);
  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setPreview(null); };

  const [create, { isLoading: creating }] = useCreateAudienceMutation();
  const [update, { isLoading: updating }] = useUpdateAudienceMutation();
  const [runPreview, { isLoading: previewing }] = usePreviewAudienceDraftMutation();

  const isPatient = form.kind === 'Patient';

  // Only send the half that applies — the server clears the other half anyway, but sending both
  // would trip the "lead conditions don't apply to a patient audience" validator.
  const payload = () => ({
    name: form.name.trim(),
    description: form.description?.trim() || null,
    kind: form.kind,

    lastVisitOp: isPatient && form.useLastVisit ? form.lastVisitOp : null,
    lastVisitDaysFrom: isPatient && form.useLastVisit ? num(form.lastVisitDaysFrom) : null,
    lastVisitDaysTo: isPatient && form.useLastVisit && form.lastVisitOp === 'Between' ? num(form.lastVisitDaysTo) : null,
    sessionsRemainingOp: isPatient && form.useSessions ? form.sessionsRemainingOp : null,
    sessionsRemaining: isPatient && form.useSessions ? num(form.sessionsRemaining) : null,
    noUpcomingOnly: isPatient && form.noUpcomingOnly,
    minDue: isPatient && form.useDue ? num(form.minDue) : null,
    patientStatus: isPatient && form.usePatientStatus ? form.patientStatus : null,
    renewalNotPitchedOnly: isPatient && form.renewalNotPitchedOnly,
    requireCompletedAppointment: isPatient ? form.requireCompletedAppointment : true,
    createdFrom: isPatient && form.useCreatedRange && form.createdFrom ? form.createdFrom : null,
    createdTo: isPatient && form.useCreatedRange && form.createdTo ? form.createdTo : null,

    leadStatus: !isPatient && form.useLeadStatus ? form.leadStatus : null,
    leadSource: !isPatient && form.useLeadSource ? form.leadSource : null,
    leadPriority: !isPatient && form.useLeadPriority ? form.leadPriority : null,
    followUpOp: !isPatient && form.useFollowUp ? form.followUpOp : null,
    followUpDaysFrom: !isPatient && form.useFollowUp ? num(form.followUpDaysFrom) : null,
    followUpDaysTo: !isPatient && form.useFollowUp && form.followUpOp === 'Between' ? num(form.followUpDaysTo) : null,
    openFollowUpOnly: !isPatient ? form.openFollowUpOnly : true,
    includeArchivedLeads: !isPatient && form.includeArchivedLeads,
    includeConvertedLeads: !isPatient && form.includeConvertedLeads,

    suppressionWindowDays: form.useSuppression ? num(form.suppressionWindowDays, 7) : null,
  });

  const doPreview = async () => {
    try {
      setPreview(await runPreview(payload()).unwrap());
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const save = async () => {
    try {
      if (isNew) await create(payload()).unwrap();
      else await update({ id: audience.id, ...payload() }).unwrap();
      toast.success(isNew ? 'Audience created.' : 'Audience updated.');
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const busy = creating || updating || previewing;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isNew ? 'New audience' : form.name}</h2>
          <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
            <div>
              <label className="form-label">Name</label>
              <input className="form-control" placeholder="Win-back — 15 to 20 days quiet"
                value={form.name} onChange={(e) => set({ name: e.target.value })} />
            </div>
            <div>
              <label className="form-label">Who</label>
              <select className="form-control" value={form.kind} onChange={(e) => set({ kind: e.target.value })}>
                <option value="Patient">Patients</option>
                <option value="Lead">Leads</option>
              </select>
            </div>
          </div>

          <div>
            <label className="form-label">Description (optional)</label>
            <input className="form-control" value={form.description}
              onChange={(e) => set({ description: e.target.value })} />
          </div>

          <div className="td-muted" style={{ fontSize: '0.76rem' }}>
            All the conditions you tick must be true. Leave one unticked to ignore it.
          </div>

          {isPatient ? (
            <>
              <Row checked={form.useLastVisit} onToggle={(v) => set({ useLastVisit: v })} label="Last completed appointment was">
                <select className="form-control" style={{ width: 140 }} value={form.lastVisitOp}
                  onChange={(e) => set({ lastVisitOp: e.target.value })}>
                  <option value="Between">between</option>
                  <option value="Gte">at least</option>
                  <option value="Eq">exactly</option>
                  <option value="Lte">within the last</option>
                </select>
                <input className="form-control" style={{ width: 70 }} type="number" min="0"
                  value={form.lastVisitDaysFrom} onChange={(e) => set({ lastVisitDaysFrom: e.target.value })} />
                {form.lastVisitOp === 'Between' && (
                  <>
                    <span className="td-muted" style={{ fontSize: '0.8rem' }}>and</span>
                    <input className="form-control" style={{ width: 70 }} type="number" min="0"
                      value={form.lastVisitDaysTo} onChange={(e) => set({ lastVisitDaysTo: e.target.value })} />
                  </>
                )}
                <span className="td-muted" style={{ fontSize: '0.8rem' }}>days ago</span>
              </Row>
              {form.useLastVisit && form.lastVisitOp === 'Between' && (
                <div className="td-muted" style={{ fontSize: '0.72rem', marginTop: -6, marginLeft: 24 }}>
                  The first number is the older one — “between 20 and 15 days ago”.
                </div>
              )}

              <Row checked={form.useSessions} onToggle={(v) => set({ useSessions: v })} label="Sessions remaining">
                <select className="form-control" style={{ width: 110 }} value={form.sessionsRemainingOp}
                  onChange={(e) => set({ sessionsRemainingOp: e.target.value })}>
                  <option value="Lte">≤</option>
                  <option value="Eq">=</option>
                  <option value="Gte">≥</option>
                </select>
                <input className="form-control" style={{ width: 70 }} type="number" min="0"
                  value={form.sessionsRemaining} onChange={(e) => set({ sessionsRemaining: e.target.value })} />
              </Row>

              <Row checked={form.useDue} onToggle={(v) => set({ useDue: v })} label="Owes at least">
                <input className="form-control" style={{ width: 110 }} type="number" min="1"
                  value={form.minDue} onChange={(e) => set({ minDue: e.target.value })} />
                <span className="td-muted" style={{ fontSize: '0.8rem' }}>₹</span>
              </Row>

              <Row checked={form.usePatientStatus} onToggle={(v) => set({ usePatientStatus: v })} label="Patient status is">
                <select className="form-control" style={{ width: 180 }} value={form.patientStatus}
                  onChange={(e) => set({ patientStatus: e.target.value })}>
                  {PATIENT_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Row>

              <Row checked={form.useCreatedRange} onToggle={(v) => set({ useCreatedRange: v })} label="New patients — created between">
                <input className="form-control" style={{ width: 150 }} type="date"
                  value={form.createdFrom} onChange={(e) => set({ createdFrom: e.target.value })} />
                <span className="td-muted" style={{ fontSize: '0.8rem' }}>and</span>
                <input className="form-control" style={{ width: 150 }} type="date"
                  value={form.createdTo} onChange={(e) => set({ createdTo: e.target.value })} />
              </Row>

              <Check label="Has nothing booked ahead" checked={form.noUpcomingOnly}
                onChange={(v) => set({ noUpcomingOnly: v })} />
              <Check label="Renewal not pitched yet" checked={form.renewalNotPitchedOnly}
                onChange={(v) => set({ renewalNotPitchedOnly: v })} />
              <Check label="Must have attended at least once"
                hint="Keeps people who have never actually visited out of win-back messages."
                checked={form.requireCompletedAppointment}
                onChange={(v) => set({ requireCompletedAppointment: v })} />
            </>
          ) : (
            <>
              <Row checked={form.useLeadStatus} onToggle={(v) => set({ useLeadStatus: v })} label="Lead status is">
                <select className="form-control" style={{ width: 220 }} value={form.leadStatus}
                  onChange={(e) => set({ leadStatus: e.target.value })}>
                  {LEAD_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Row>

              <Row checked={form.useLeadSource} onToggle={(v) => set({ useLeadSource: v })} label="Came from">
                <select className="form-control" style={{ width: 180 }} value={form.leadSource}
                  onChange={(e) => set({ leadSource: e.target.value })}>
                  {LEAD_SOURCE.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Row>

              <Row checked={form.useLeadPriority} onToggle={(v) => set({ useLeadPriority: v })} label="Priority is">
                <select className="form-control" style={{ width: 140 }} value={form.leadPriority}
                  onChange={(e) => set({ leadPriority: e.target.value })}>
                  {LEAD_PRIORITY.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Row>

              <Row checked={form.useFollowUp} onToggle={(v) => set({ useFollowUp: v })} label="Follow-up date was">
                <select className="form-control" style={{ width: 140 }} value={form.followUpOp}
                  onChange={(e) => set({ followUpOp: e.target.value })}>
                  <option value="Gte">at least</option>
                  <option value="Between">between</option>
                  <option value="Eq">exactly</option>
                  <option value="Lte">within the last</option>
                </select>
                <input className="form-control" style={{ width: 70 }} type="number" min="0"
                  value={form.followUpDaysFrom} onChange={(e) => set({ followUpDaysFrom: e.target.value })} />
                {form.followUpOp === 'Between' && (
                  <>
                    <span className="td-muted" style={{ fontSize: '0.8rem' }}>and</span>
                    <input className="form-control" style={{ width: 70 }} type="number" min="0"
                      value={form.followUpDaysTo} onChange={(e) => set({ followUpDaysTo: e.target.value })} />
                  </>
                )}
                <span className="td-muted" style={{ fontSize: '0.8rem' }}>days ago</span>
              </Row>

              <Check label="Only follow-ups still open" checked={form.openFollowUpOnly}
                onChange={(v) => set({ openFollowUpOnly: v })} />
              <Check label="Include archived leads" checked={form.includeArchivedLeads}
                onChange={(v) => set({ includeArchivedLeads: v })} />
              <Check label="Include leads already converted to patients"
                hint="Usually off — they're a patient now, and you'd reach the same person twice."
                checked={form.includeConvertedLeads}
                onChange={(v) => set({ includeConvertedLeads: v })} />
            </>
          )}

          <Row checked={form.useSuppression} onToggle={(v) => set({ useSuppression: v })}
            label="Skip anyone messaged in the last">
            <input className="form-control" style={{ width: 70 }} type="number" min="1" max="365"
              value={form.suppressionWindowDays} onChange={(e) => set({ suppressionWindowDays: e.target.value })} />
            <span className="td-muted" style={{ fontSize: '0.8rem' }}>days</span>
          </Row>

          {preview && <PreviewPanel preview={preview} kind={form.kind} />}
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-outline" onClick={doPreview} disabled={busy}>
            <Eye size={15} /> {previewing ? 'Checking…' : 'Preview'}
          </button>
          <button className="btn btn-primary" onClick={save} disabled={busy || !form.name.trim()}>
            <Save size={15} /> {isNew ? 'Create' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewPanel({ preview, kind }) {
  const noun = kind === 'Patient' ? 'patient' : 'lead';
  return (
    <div className="card card-body" style={{ background: 'var(--bg-hover)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <Users size={16} />
        <span style={{ fontSize: '1.4rem', fontWeight: 700 }}>{preview.totalCount.toLocaleString()}</span>
        <span className="td-muted">{noun}{preview.totalCount === 1 ? '' : 's'} match right now</span>
      </div>

      {preview.capExceeded && (
        <div style={{ fontSize: '0.78rem', color: 'var(--danger)', marginTop: 6, display: 'flex', gap: 6 }}>
          <AlertTriangle size={13} />
          Stopped counting at {preview.effectiveCap.toLocaleString()} — the real number is higher.
          Tighten the rule before using it.
        </div>
      )}

      {(preview.skippedDuplicatePhone > 0 || preview.skippedInvalidPhone > 0) && (
        <div className="td-muted" style={{ fontSize: '0.74rem', marginTop: 4 }}>
          {preview.skippedDuplicatePhone > 0 && `${preview.skippedDuplicatePhone} collapsed as duplicate phone numbers. `}
          {preview.skippedInvalidPhone > 0 && `${preview.skippedInvalidPhone} skipped for an unusable phone number.`}
        </div>
      )}

      {preview.sample?.length > 0 && (
        <div className="tbl-wrap" style={{ marginTop: 10, maxHeight: 220, overflowY: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Name</th><th>Phone</th>
                {kind === 'Patient' ? <><th>Sessions left</th><th>Last visit</th></> : <th>Follow-up</th>}
              </tr>
            </thead>
            <tbody>
              {preview.sample.map((r) => (
                <tr key={r.entityId}>
                  <td>{r.fullName}</td>
                  <td className="td-mono">{r.phone}</td>
                  {kind === 'Patient' ? (
                    <>
                      <td>{r.sessionsRemaining ?? '—'}</td>
                      <td>{r.lastVisitDaysAgo != null ? `${r.lastVisitDaysAgo}d ago` : '—'}</td>
                    </>
                  ) : (
                    <td>{r.followUpAt ? new Date(r.followUpAt).toLocaleDateString() : '—'}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** A condition: the checkbox enables it, and its inputs only appear when ticked. */
function Row({ checked, onToggle, label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', minWidth: 210 }}>
        <input type="checkbox" checked={checked} onChange={(e) => onToggle(e.target.checked)} />
        <span style={{ fontSize: '0.85rem' }}>{label}</span>
      </label>
      {checked && children}
    </div>
  );
}

function Check({ label, hint, checked, onChange }) {
  return (
    <div>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span style={{ fontSize: '0.85rem' }}>{label}</span>
      </label>
      {hint && <div className="td-muted" style={{ fontSize: '0.72rem', marginLeft: 22 }}>{hint}</div>}
    </div>
  );
}
